import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { ArrowLeft, Download, Copy, Check, Loader2, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DeckStats, DrawSimulator, Tile, TileGrid } from '../components/sidebar';
import { LanguageBadge } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import {
  PRELOAD_DECKS,
  CHART_OPTIONS,
  TILE_CONFIG,
} from '../constants';
import {
  getProxyImageUrl,
  validateDeck,
  getLevelChartData,
  getCharacterChartData,
  sortCardsForDisplay,
  normalizeCard,
  shuffleArray,
  expandDeckByCount,
} from '../utils';
import '../components/DeckVisualizer.css';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

/**
 * DeckView page - Read-only view of a deck
 * Shows deck cards, stats, and draw simulator without editing capabilities
 */
export default function DeckView() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Deck state
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deckInfo, setDeckInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  // Tile collapse states
  const [collapseStats, setCollapseStats] = useState(false);
  const [collapseDrawSimulator, setCollapseDrawSimulator] = useState(false);

  // Layout mode state for TileGrid
  const [layoutMode, setLayoutMode] = useState('default');

  // Draw simulator state
  const [drawnCards, setDrawnCards] = useState([]);
  const [remainingDeck, setRemainingDeck] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mulliganCount, setMulliganCount] = useState(0);

  // Load deck function
  const loadDeck = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Find deck in PRELOAD_DECKS
      const deck = PRELOAD_DECKS.find(d => d.id === deckId);
      
      if (!deck) {
        setError(t('deckView.deckNotFound') || 'Deck not found');
        setLoading(false);
        return;
      }

      setDeckInfo(deck);

      if (!deck.deckCode) {
        setError(t('deckView.invalidDeckCode') || 'Deck code not available');
        setLoading(false);
        return;
      }

      // Decode deck
      const deckString = decodeURIComponent(escape(atob(deck.deckCode.trim())));
      const deckData = JSON.parse(deckString);

      if (deckData.version === 2 && deckData.cards) {
        // Fetch card details
        const cardPromises = deckData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}?lang=${language}`)
            .then(res => res.json())
            .then(card => {
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false,
              };
            })
            .catch(() => null)
        );

        const fetchedCards = await Promise.all(cardPromises);
        setCards(fetchedCards.filter(Boolean));
      }
    } catch (err) {
      console.error('Failed to load deck:', err);
      setError(t('sampleDecks.deckLoadError') || 'Failed to load deck');
    } finally {
      setLoading(false);
    }
  }, [deckId, language, t]);

  // Load deck on mount or when dependencies change
  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  // Deck validation
  const deckValidation = useMemo(() => validateDeck(cards), [cards]);

  // Chart data
  const levelChartData = useCallback(() => getLevelChartData(deckValidation.stats), [deckValidation.stats]);
  const characterChartData = useCallback(() => getCharacterChartData(deckValidation.stats), [deckValidation.stats]);

  // Draw simulator functions
  const drawInitialHand = () => {
    const fullDeck = shuffleArray(expandDeckByCount(cards));
    const hand = fullDeck.slice(0, 6);
    const remaining = fullDeck.slice(6);
    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setHasDrawn(true);
    setMulliganCount(0);
  };

  const mulligan = () => {
    if (mulliganCount >= 1) {
      alert(t('errors.mulliganLimit') || 'You can only mulligan once!');
      return;
    }
    if (remainingDeck.length < 6) {
      alert(t('errors.notEnoughCards') || 'Not enough cards left in deck to mulligan!');
      return;
    }
    const hand = remainingDeck.slice(0, 6);
    const remaining = remainingDeck.slice(6);
    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setMulliganCount(mulliganCount + 1);
  };

  const resetDraw = () => {
    setMulliganCount(0);
    drawInitialHand();
  };

  // Copy deck code
  const copyDeckCode = async () => {
    if (deckInfo?.deckCode) {
      await navigator.clipboard.writeText(deckInfo.deckCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Get deck name
  const getDeckName = () => {
    if (!deckInfo) return 'Loading...';
    if (language === 'JP' && deckInfo.name_ja) return deckInfo.name_ja;
    return deckInfo.name;
  };

  // Get deck description
  const getDeckDescription = () => {
    if (!deckInfo) return '';
    if (language === 'JP' && deckInfo.description_ja) return deckInfo.description_ja;
    return deckInfo.description || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-48px-40px)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-48px-40px)] gap-4">
        <p className="text-destructive text-lg">{error}</p>
        <Button onClick={() => navigate('/decks')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('deckView.backToDecks') || 'Back to Sample Decks'}
        </Button>
      </div>
    );
  }

  return (
    <div className="deck-builder-container h-full flex flex-col overflow-hidden">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-[#3c618a] to-[#7a67ba] text-white flex-shrink-0">
        <div className="max-w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/decks')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <Layers className="h-6 w-6" />
                  <h1 className="text-xl font-bold">{getDeckName()}</h1>
                </div>
                <p className="text-sm text-white/70 mt-0.5">
                  {getDeckDescription() || `${deckValidation.stats.totalCards} ${t('common.cards') || 'cards'}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={copyDeckCode}
                className="border-white/30 text-white hover:bg-white/20"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    {t('common.copied') || 'Copied!'}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    {t('deckView.copyDeckCode') || 'Copy Code'}
                  </>
                )}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/deck-builder?load=${encodeURIComponent(deckInfo?.deckCode || '')}`)}
              >
                <Download className="h-4 w-4 mr-1" />
                {t('deckView.loadInBuilder') || 'Load in Builder'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with TileGrid */}
      <div className="flex-1 overflow-hidden p-3">
        <TileGrid
          layoutMode={layoutMode}
          onLayoutModeChange={setLayoutMode}
          deckAreaContent={
            <div className="deck-area flex flex-col min-h-0 h-full bg-white rounded-lg shadow-sm border border-border overflow-hidden">
              {/* Card Grid */}
              <div className="flex-1 overflow-auto p-3">
                <div className="card-grid" id="cardGrid">
                  {sortCardsForDisplay(cards).map((card) => {
                    const cardId = card.cardNumber || card.number || card.id;
                    const imageUrl = getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full);
                    const rarityText = typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '';
                    const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
                    const featureValue = card.feature || '';
                    const isScene = featureValue === 'scene';
                    const cardLanguage = card.language || card._fallbackLanguage;

                    return (
                      <div
                        key={cardId}
                        className={`card-wrapper${isScene ? ' scene-card' : ''}${card._fallbackLanguage ? ' language-fallback' : ''}`}
                      >
                        {imageUrl && <img src={imageUrl} alt={card.name} className="card-image" />}
                        <LanguageBadge cardLanguage={cardLanguage} selectedLanguage={language} />
                        <div className="card-info">
                          <div className="card-name">{card.name}</div>
                          <div className="card-meta">{card.number}</div>
                          {rarityText && <div className="card-rarity">{rarityText}</div>}
                          {typeText && <div className="card-type">{typeText}</div>}
                        </div>
                        <div className="card-count">x{card.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        >
          {/* Tile: Deck Stats */}
          <Tile
            id={TILE_CONFIG.deckStats.id}
            icon={TILE_CONFIG.deckStats.icon}
            title={t(TILE_CONFIG.deckStats.titleKey) || TILE_CONFIG.deckStats.defaultTitle}
            isCollapsed={collapseStats}
            onToggle={() => setCollapseStats(!collapseStats)}
            heightUnits={TILE_CONFIG.deckStats.defaultHeight}
            minHeight={TILE_CONFIG.deckStats.minHeight}
            maxHeight={TILE_CONFIG.deckStats.maxHeight}
          >
            <DeckStats
              isCollapsed={false}
              onToggle={() => {}}
              deckValidation={deckValidation}
              getLevelChartData={levelChartData}
              getCharacterChartData={characterChartData}
              chartOptions={CHART_OPTIONS}
              cards={cards}
              t={t}
              hideHeader={true}
            />
          </Tile>

          {/* Tile: Draw Simulator */}
          <Tile
            id={TILE_CONFIG.drawSimulator.id}
            icon={TILE_CONFIG.drawSimulator.icon}
            title={t(TILE_CONFIG.drawSimulator.titleKey) || TILE_CONFIG.drawSimulator.defaultTitle}
            isCollapsed={collapseDrawSimulator}
            onToggle={() => setCollapseDrawSimulator(!collapseDrawSimulator)}
            heightUnits={TILE_CONFIG.drawSimulator.defaultHeight}
            minHeight={TILE_CONFIG.drawSimulator.minHeight}
            maxHeight={TILE_CONFIG.drawSimulator.maxHeight}
          >
            <DrawSimulator
              isCollapsed={false}
              onToggle={() => {}}
              drawnCards={drawnCards}
              remainingDeck={remainingDeck}
              hasDrawn={hasDrawn}
              mulliganCount={mulliganCount}
              deckHasCards={cards.length > 0}
              onDrawInitialHand={drawInitialHand}
              onMulligan={mulligan}
              onResetDraw={resetDraw}
              getProxyImageUrl={getProxyImageUrl}
              t={t}
              hideHeader={true}
            />
          </Tile>
        </TileGrid>
      </div>
    </div>
  );
}
